import React, { useState, useMemo } from 'react';

const PoliticalVoting = ({ userData, currentUser, activeVote }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voteAmount, setVoteAmount] = useState("");
  const [proposalText, setProposalText] = useState("");
  const [voteDays, setVoteDays] = useState(7);

  const BASE_FEE = 100;
  const DAILY_RATE = 10;
  const currentInitiationCost = BASE_FEE + (voteDays * DAILY_RATE);

  const THEME_TEAL = "#50e3c2";
  const THEME_RED = "#f44336";
  const TEXT_GREY = "#888";
  const DEFAULT_GREY = "#333";

  const stats = useMemo(() => {
    if (!activeVote) return { sPct: 0, oPct: 0 };
    const total = (activeVote.support || 0) + (activeVote.oppose || 0);
    if (total === 0) return { sPct: 50, oPct: 50 };
    return {
      sPct: (activeVote.support / total) * 100,
      oPct: (activeVote.oppose / total) * 100
    };
  }, [activeVote]);

 const handleInitiateVote = async () => {

    const points = userData?.userPoliticalPoints || 0;
    if (!proposalText) return alert("Proposal text required.");
    if (points < currentInitiationCost) return alert("Insufficient Vote Power.");

    setIsSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch("https://us-central1-stockmarket-83a73.cloudfunctions.net/initiatePoliticalVote", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ 
          description: proposalText,
          days: voteDays 
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText);
      }

      setProposalText("");

    } catch (err) {
      console.error("Initiation failed:", err.message);
      alert(`System Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCastVote = async (side) => {
    const amt = Math.floor(Number(voteAmount));
    if (amt <= 0) return;
    if (amt > (userData?.userPoliticalPoints || 0)) return alert("Insufficient PP balance.");

    setIsSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch("https://us-central1-stockmarket-83a73.cloudfunctions.net/castPoliticalVote", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ 
          voteId: "current_resolution", 
          amount: amt, 
          side 
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText);
      }

      setVoteAmount("");
    } catch (err) {
      console.error("Vote failed:", err.message);
      alert(`Vote Rejected: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOverBalance = Number(voteAmount) > (userData?.userPoliticalPoints || 0);

  return (
    
    <div style={{ width: '99%', fontFamily: 'JetBrains Mono, monospace' }}>
      {(!activeVote || activeVote.status !== "active") ? (
        /* PHASE 1: NO ACTIVE VOTE */
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <h3 style={{ color: THEME_TEAL, margin: 0, fontSize: '1rem', letterSpacing: '2px' }}>BOARDROOM</h3>
              <div style={{ fontSize: '0.6rem', color: THEME_RED, marginTop: '2px' }}>STATUS: OPEN</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.55rem', color: TEXT_GREY }}>TOTAL COST</div>
              <div style={{ fontSize: '0.9rem', color: THEME_TEAL }}>{currentInitiationCost} VP</div>
            </div>
          </div>

          {/* DURATION SELECTOR */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.75rem', color: TEXT_GREY, display: 'block', marginBottom: '8px' }}>
              SET PROPOSAL LIFESPAN ({voteDays} DAYS)
            </label>
            <input 
              type="range" min="1" max="14" 
              value={voteDays} 
              onChange={(e) => setVoteDays(parseInt(e.target.value))}
              style={{ width: '99%', accentColor: THEME_TEAL, cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#444', marginTop: '4px' }}>
              <span>1 DAY (MIN)</span>
              <span>14 DAYS (MAX)</span>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.6rem', color: TEXT_GREY, display: 'block', marginBottom: '6px' }}>
              PROPOSE NEW RESOLUTION
            </label>
            <textarea 
              placeholder="ENTER PROPOSAL..."
              value={proposalText}
              onChange={(e) => setProposalText(e.target.value)}
              style={{ 
                width: '100%', background: '#000', border: '1px solid #222', color: '#fff', 
                padding: '12px', fontSize: '0.75rem', minHeight: '100px', outline: 'none',
                boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit',
                borderLeft: `2px solid ${DEFAULT_GREY}`
              }}
            />
          </div>

          <button 
            className="jetbrains-btn"
            disabled={isSubmitting || (userData?.userPoliticalPoints || 0) < currentInitiationCost || !proposalText}
            onClick={handleInitiateVote}
            style={{ 
              width: '100%', padding: '14px', fontWeight: 'bold', letterSpacing: '2px', fontSize: '0.75rem',
              color: THEME_TEAL, borderColor: THEME_TEAL,
              background: isSubmitting ? 'transparent' : `${THEME_TEAL}10`,
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? "INITIATING..." : `INITIATE FOR ${currentInitiationCost} VP`}
          </button>
        </div>
      ) : (
        /* PHASE 2: VOTE IN PROGRESS */
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
            <div style={{ fontSize: '1.0rem', color: THEME_TEAL, letterSpacing: '1px' }}>CURRENT PROPOSAL</div>
<div style={{ fontSize: '1.0rem', color: THEME_RED }}>
  EXPIRES: {activeVote.expiresAt?.seconds 
    ? new Date(activeVote.expiresAt.seconds * 1000).toLocaleDateString() 
    : "LOADING..."}
</div>
          </div>

          <div style={{ 
            fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', padding: '15px', 
            background: 'rgba(255,255,255,0.02)', border: '1px solid #1a1a1a', borderLeft: `2px solid ${THEME_TEAL}`,
            marginBottom: '20px'
          }}>
            "{activeVote.description}"
          </div>

          <div style={{ marginBottom: '25px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', marginBottom: '8px' }}>
              <span style={{ color: THEME_TEAL }}>SUPPORT: {activeVote.support?.toLocaleString()} VP</span>
              <span style={{ color: THEME_RED }}>OPPOSE: {activeVote.oppose?.toLocaleString()} VP</span>
            </div>
            <div style={{ height: '6px', background: '#111', width: '100%', display: 'flex', border: '1px solid #222' }}>
              <div style={{ width: `${stats.sPct}%`, background: THEME_TEAL, transition: 'width 1s ease' }} />
              <div style={{ width: `${stats.oPct}%`, background: THEME_RED, transition: 'width 1s ease' }} />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '0.55rem', color: '#444', marginBottom: '6px', letterSpacing: '1px' }}>VOTES</div>
            <div style={{ maxHeight: '100px', overflowY: 'auto', background: '#050505', border: '1px solid #1a1a1a' }} className="custom-scrollbar">
              {(!activeVote.voters || activeVote.voters.length === 0) ? (
                <div style={{ fontSize: '0.6rem', color: '#222', textAlign: 'center', padding: '15px' }}>NO DATA</div>
              ) : (
                activeVote.voters.map((v, i) => (
                  <div key={i} style={{ 
                    display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', padding: '8px 12px',
                    borderBottom: '1px solid #111', color: v.side === 'support' ? THEME_TEAL : THEME_RED 
                  }}>
                    <span>{v.name.toUpperCase()}</span>
                    <span>{v.side === 'support' ? '+' : '-'}{v.amount.toLocaleString()}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <input 
              type="number" 
              placeholder="SUBMIT VOTE POWER..."
              value={voteAmount}
              onChange={(e) => setVoteAmount(e.target.value)}
              style={{ 
                width: '100%', background: '#000', border: `1px solid ${isOverBalance ? THEME_RED : '#222'}`, 
                color: isOverBalance ? THEME_RED : THEME_TEAL, padding: '12px', fontSize: '1rem', 
                outline: 'none', textAlign: 'right', fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              disabled={isSubmitting || !voteAmount || isOverBalance}
              onClick={() => handleCastVote('support')}
              style={{ flex: 1, padding: '14px', border: `1px solid ${THEME_TEAL}`, background: `${THEME_TEAL}15`, color: THEME_TEAL, cursor: 'pointer', fontWeight: 'bold' }}
            >SUPPORT</button>
            <button 
              disabled={isSubmitting || !voteAmount || isOverBalance}
              onClick={() => handleCastVote('oppose')}
              style={{ flex: 1, padding: '14px', border: `1px solid ${THEME_RED}`, background: `${THEME_RED}15`, color: THEME_RED, cursor: 'pointer', fontWeight: 'bold' }}
            >OPPOSE</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoliticalVoting;